import { useState } from 'react';
import { MdChevronLeft, MdChevronRight } from 'react-icons/md';
import type { SATheme, SAProduct } from '../../types/stylingAssistant.types';

// ─── Props ───────────────────────────────────────────────────────────────────

interface SAProductCarouselProps {
  theme: SATheme;
  title: string;
  products: SAProduct[];
  groupIndex: number;
  isVisible: boolean;
  isNextLoading: boolean;
  carouselRef: (el: HTMLDivElement | null) => void;
  groupRef: (el: HTMLDivElement | null) => void;
  onScrollLeft: () => void;
  onScrollRight: () => void;
}

// ─── Single Product Card (Featured) ─────────────────────────────────────────

function SingleProductCard({
  product,
  theme,
}: {
  product: SAProduct;
  theme: SATheme;
}) {
  return (
    <div
      className="flex gap-4 p-3 rounded-xl transition-all hover:bg-white/[0.03] cursor-pointer"
      style={{
        border: `1px solid rgba(255, 255, 255, 0.06)`,
      }}
    >
      {/* Image */}
      <div className="relative w-28 h-36 rounded-lg overflow-hidden flex-shrink-0">
        <div className="w-full h-full bg-white">
          <img
            src={product.image}
            alt={product.lowercase_title}
            className="w-full h-full object-top bg-white object-cover"
            loading="lazy"
          />
        </div>
      </div>

      {/* Details */}
      <div className="flex flex-col justify-between min-w-0 flex-1 py-1">
        <div className="flex flex-col gap-1.5">
          <p
            className="text-sm font-medium capitalize line-clamp-2"
            style={{ color: theme.text }}
          >
            {product.lowercase_title}
          </p>
          <p className="text-base font-bold text-[#EE4423]">
            ${product.modified_price?.toLocaleString()}
          </p>
          <div className="flex items-center gap-1.5 flex-wrap mt-1">
            {product.colors_string && (
              <span className="text-[10px] px-2 py-0.5 rounded-full capitalize bg-white/[0.06] text-gray-400">
                {product.colors_string}
              </span>
            )}
            {product.gender && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.06] text-gray-400">
                {product.gender}
              </span>
            )}
            {product.category_llm && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.06] text-gray-400">
                {product.category_llm}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Carousel Product Card ───────────────────────────────────────────────────

function ProductCard({
  product,
  theme,
}: {
  product: SAProduct;
  theme: SATheme;
}) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      className="flex-shrink-0"
      style={{ width: '160px' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className="relative mb-2 cursor-pointer group rounded-lg overflow-hidden"
        style={{ height: '210px' }}
      >
        <div className="w-full h-full bg-white">
          <img
            src={product.image}
            alt={product.lowercase_title}
            className="w-full h-full object-top object-cover transition-transform duration-300 group-hover:scale-105"
            loading="lazy"
          />
        </div>

        {/* Hover overlay gradient */}
        <div
          className="absolute inset-0 transition-opacity duration-300 pointer-events-none"
          style={{
            opacity: isHovered ? 1 : 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 50%)',
          }}
        />

        {/* Price badge on hover */}
        <div
          className="absolute bottom-2 left-2 transition-all duration-300"
          style={{
            opacity: isHovered ? 1 : 0,
            transform: isHovered ? 'translateY(0)' : 'translateY(8px)',
          }}
        >
          <span className="text-xs font-bold text-white bg-black/60 backdrop-blur-sm px-2 py-1 rounded-md">
            ${product.modified_price?.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Product info below image */}
      <div>
        <p
          className="text-xs font-normal line-clamp-2 mb-0.5 capitalize"
          style={{ color: theme.text }}
        >
          {product.lowercase_title}
        </p>
        <p className="text-sm font-semibold text-[#EE4423]">
          ${product.modified_price?.toLocaleString()}
        </p>
        {product.colors_string && (
          <p className="text-[10px] text-gray-500 capitalize mt-0.5">
            {product.colors_string}
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

export default function SAProductCarousel({
  theme,
  title,
  products,
  groupIndex,
  isVisible,
  isNextLoading,
  carouselRef,
  groupRef,
  onScrollLeft,
  onScrollRight,
}: SAProductCarouselProps) {
  const isSingleProduct = products.length === 1;
  const showArrows = products.length > 3;

  return (
    <div key={groupIndex}>
      {/* Loading dots for next category */}
      {isNextLoading && (
        <div className="flex items-center gap-2 py-3">
          <div
            className="w-1.5 h-1.5 rounded-full animate-bounce bg-[#EE4423]"
            style={{ animationDelay: '0ms' }}
          />
          <div
            className="w-1.5 h-1.5 rounded-full animate-bounce bg-[#EE4423]"
            style={{ animationDelay: '150ms' }}
          />
          <div
            className="w-1.5 h-1.5 rounded-full animate-bounce bg-[#EE4423]"
            style={{ animationDelay: '300ms' }}
          />
        </div>
      )}

      {/* Category content */}
      <div
        ref={groupRef}
        className="transition-all duration-700 ease-out"
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
          maxHeight: isVisible ? '1000px' : '0',
          overflow: isVisible ? 'visible' : 'hidden',
        }}
      >
        {/* Category Title */}
        <h3
          className="text-sm font-semibold mb-3 capitalize tracking-wide"
          style={{ color: theme.textSecondary }}
        >
          {title}
          <span className="ml-2 text-xs font-normal" style={{ color: theme.textMuted }}>
            ({products.length} {products.length === 1 ? 'item' : 'items'})
          </span>
        </h3>

        {/* Single product: featured card */}
        {isSingleProduct ? (
          <SingleProductCard product={products[0]} theme={theme} />
        ) : (
          /* Multiple products: horizontal carousel */
          <div className="relative group/carousel">
            {/* Left Arrow */}
            {showArrows && (
              <button
                onClick={onScrollLeft}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 opacity-0 group-hover/carousel:opacity-100 transition-opacity duration-300 cursor-pointer"
                style={{ marginTop: '-30px' }}
                aria-label="Scroll left"
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform bg-[#2a2a2a] border border-white/10"
                >
                  <MdChevronLeft size={18} className="text-white" />
                </div>
              </button>
            )}

            {/* Carousel */}
            <div
              ref={carouselRef}
              className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide scroll-smooth"
            >
              {products.map((product) => (
                <ProductCard
                  key={product.sku}
                  product={product}
                  theme={theme}
                />
              ))}
            </div>

            {/* Right Arrow */}
            {showArrows && (
              <button
                onClick={onScrollRight}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 opacity-0 group-hover/carousel:opacity-100 transition-opacity duration-300 cursor-pointer"
                style={{ marginTop: '-30px' }}
                aria-label="Scroll right"
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-transform bg-[#2a2a2a] border border-white/10"
                >
                  <MdChevronRight size={18} className="text-white" />
                </div>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
